import { CSSProperties } from "react";
import useBrand from 'Hooks/useBrand';
import ModalSymphony from "elements/ModalSymphony";
import ButtonSymphony from "elements/ButtonSymphony";
import Colors from "modules/Colors";
import Sty from "modules/Sty";
import useScreen from "Hooks/useScreen";
import { SocialConnectType } from "modules/Const";
import { useDashboard } from "Hooks/useDashboard";
import { makeKey } from "modules/Utils";

interface PlatformsConnectorProps {
    isOpen: boolean;
    closeModal: () => void;
}

export interface PlatformType {
    id: string;
    name: string;
    needsConnection: boolean;
    imageAsset: string;
    username: string | undefined | null;
}

const PlatformsConnector = ({
    isOpen,
    closeModal,
}: PlatformsConnectorProps) => {
    const { mobileView } = useScreen();
    const { currentBrand } = useBrand();
    const {
        dashboardTrackedPlatforms,
        setSelectedPlatformForConnect
    } = useDashboard();

    /** Transform dashboard platforms into PlatformType format */
    const currentBrandConnections = !currentBrand ? [] : Object.values(dashboardTrackedPlatforms)
        .filter(platform => platform.type === 'platforms')
        .reduce((unique: PlatformType[], platform) => {
            const existingPlatform = unique.find(p => p.id === platform.platformId);
            if (existingPlatform) return unique;

            return [...unique, {
                id: platform.platformId,
                name: platform.platformName,
                needsConnection: platform.needsConnection(currentBrand),
                imageAsset: platform.icon,
                username: platform.getUsername ? platform.getUsername(currentBrand) : null
            }];
        }, []);

    if (!currentBrand) {
        return null;
    }

    // const onSave = async (platform: string) => {
    //     track('Linked Platform',
    //         {
    //             brand_id: currentBrand.id,
    //             brand_slug: currentBrand.slug,
    //             brand_name: currentBrand.name,
    //             platform: platform,
    //             page: "Dashboard",
    //             from: "connector"
    //         });

    //     setUserProperty(`${platform}_linked`, true)
    //     await getBrand()
    // }

    const selectedPlatform = (platformType: PlatformType) => {
        setSelectedPlatformForConnect(platformType.id as SocialConnectType);
    }

    const renderCheckmark = () => {
        return (
            <img
                className="platformsConnectorCheckmark"
                style={{
                    position: 'absolute',
                    bottom: -4,
                    right: -4,
                    width: 16,
                    height: 16
                }}
                src={require("assets/images/tick.svg").default}
                alt="tick"
            />
        )
    }

    const renderEditConnectionButton = (needsConnection: boolean, platform: PlatformType) => {
        return (
            <ButtonSymphony
                className="platformsConnectorModalEditButton"
                onClick={() => selectedPlatform(platform)}
                variant="text"
            >
                {needsConnection ? 'Connect' : 'Edit'}
            </ButtonSymphony>
        )
    }

    return (
        <>
            <ModalSymphony
                className="platformsConnectorModal"
                isOpen={isOpen}
                onClose={closeModal}
                title="Connect Your Platforms"
                subtitle="Update the platforms connected to your profile."
                maxWidth={420}
                fullScreen={mobileView}
            >
                <div className="platformsConnectorModalContent" style={{ width: '100%', borderTop: Sty.border }}>
                    {currentBrandConnections
                        .sort((a: PlatformType, b: PlatformType) => {
                            if (a.needsConnection && !b.needsConnection) {
                                return 1
                            }

                            if (b.needsConnection && !a.needsConnection) {
                                return -1
                            }

                            return 0
                        })
                        .map((platform: PlatformType, index: Number) => {
                            const {
                                id,
                                name,
                                needsConnection,
                                imageAsset,
                                username
                            } = platform
                            return (
                                <div
                                    key={makeKey('platform', id)}
                                    className="platformsConnectorModalCard"
                                    style={sty.platformsConnectorModalCard}
                                >
                                    <div
                                        className="platformsConnectorImage"
                                        style={{
                                            display: 'flex',
                                            position: 'relative',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <img src={imageAsset} alt={name} />
                                        {!needsConnection && renderCheckmark()}
                                    </div>
                                    <div className="platformName" style={{ marginRight: 'auto' }}>
                                        {name}
                                    </div>
                                    {!needsConnection &&
                                        <div className="platformsConnectorModalUsernameText" style={sty.usernameText}>
                                            {username}
                                        </div>
                                    }
                                    {renderEditConnectionButton(needsConnection, platform)}
                                </div>
                            )
                        })}
                </div>
            </ModalSymphony>
        </>
    )
}

const sty: Record<string, CSSProperties> = {
    platformsConnectorModalCard: {
        display: 'flex',
        alignItems: 'center',
        padding: 16,
        borderBottom: Sty.border,
        width: '100%',
        gap: 12
    },
    usernameText: {
        paddingLeft: 12,
        color: Colors.grey,
        wordBreak: 'break-word',
        textAlign: 'right'
    }
}

export default PlatformsConnector;
